import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import { menuCategories, menuItems } from '../../../mocks/menuData';

const MenuItemPage = () => {
  const { category, item: itemSlug } = useParams<{ category: string; item: string }>();
  const [quantity, setQuantity] = useState(1);
  
  const categoryData = menuCategories.find(cat => cat.slug === category);
  const items = category ? menuItems[category as keyof typeof menuItems] || [] : [];
  const item = items.find(i => i.slug === itemSlug);

  if (!categoryData || !item) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Item Not Found</h1>
            <Link to="/menu" className="text-blue-600 hover:text-blue-700 font-semibold">
              ← Back to Menu
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleOrderOnline = () => {
    window.open('https://www.toasttab.com/the-ocean-view-84500-overseas-hwy', '_blank');
  };

  const handleCallToOrder = () => {
    window.location.href = 'tel:+13056648052';
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MenuItem",
    "name": item.name,
    "description": item.description,
    "image": item.image,
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}/${itemSlug}`,
    "inLanguage": "en-US",
    "offers": {
      "@type": "Offer",
      "price": item.price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "nutrition": item.calories ? {
      "@type": "NutritionInformation",
      "calories": `${item.calories} calories`
    } : undefined,
    "isPartOf": {
      "@type": "Menu",
      "name": `${categoryData.name} - Ocean View Inn & Sports Pub`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}`
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${item.name} | Ocean View Inn Islamorada Menu Florida Keys`}
        description={`${item.description} Available at Ocean View Inn & Sports Pub in Islamorada, Florida Keys. $${item.price}. Order now or visit us for authentic Keys dining.`}
        keywords={`${item.name}, Islamorada restaurant menu, Florida Keys food, Ocean View Inn, ${categoryData.name}`}
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}/${itemSlug}`}
        ogTitle={`${item.name} | Ocean View Inn Menu`}
        ogDescription={`${item.description} - $${item.price}`}
        ogImage="https://public.readdy.ai/ai/img_res/2460caff17ec92424c19b1ab8a276218.jpg"
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/menu/${category}/${itemSlug}`}
        structuredData={structuredData}
      />
      <Header />
      <main className="pt-20">
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="mb-8">
              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <Link to="/menu" className="hover:text-blue-600 transition-colors">Menu</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/menu/${category}`} className="hover:text-blue-600 transition-colors">
                  {categoryData.name}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-slate-900 font-medium">{item.name}</span>
              </div>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                <div className="aspect-w-4 aspect-h-3 overflow-hidden rounded-2xl">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-96 object-cover object-top"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="aspect-w-1 aspect-h-1 overflow-hidden rounded-lg">
                    <img
                      src={item.image}
                      alt={`${item.name} view 1`}
                      className="w-full h-24 object-cover object-top cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </div>
                  <div className="aspect-w-1 aspect-h-1 overflow-hidden rounded-lg">
                    <img
                      src={categoryData.image}
                      alt={`${item.name} view 2`}
                      className="w-full h-24 object-cover object-top cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  </div>
                  <div className="aspect-w-1 aspect-h-1 overflow-hidden rounded-lg bg-slate-100 flex items-center justify-center">
                    <i className="ri-add-line text-2xl text-slate-400"></i>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h1 className="text-4xl font-bold text-slate-900 mb-4 font-serif">{item.name}</h1>
                  {item.price > 0 && (
                    <div className="text-3xl font-bold text-blue-600 mb-6">
                      ${item.price.toFixed(2)}
                    </div>
                  )}
                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Category Information</h3>
                  <p className="text-slate-600 mb-4">{categoryData.description}</p>
                  <div className="flex items-center space-x-2 text-sm text-slate-500">
                    <i className={`${categoryData.icon} text-blue-600`}></i>
                    <span>{categoryData.name}</span>
                  </div>
                </div>

                {item.price > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Quantity</h3>
                    <div className="flex items-center space-x-4 mb-6">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-50 transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="text-xl font-semibold w-8 text-center">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-50 transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <div className="text-right mb-4">
                      <span className="text-lg text-slate-600">Total: </span>
                      <span className="text-2xl font-bold text-blue-600">
                        ${(item.price * quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <button
                    onClick={handleOrderOnline}
                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-line mr-2"></i>
                    Order Online
                  </button>
                  <button
                    onClick={handleCallToOrder}
                    className="w-full bg-slate-900 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-slate-800 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call to Order: (305) 664-8052
                  </button>
                </div>

                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 mb-3">Restaurant Information</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center space-x-2">
                      <i className="ri-map-pin-line text-blue-600"></i>
                      <span>84500 Overseas Hwy, Islamorada, FL 33036</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-time-line text-blue-600"></i>
                      <span>Open Daily: 11 AM - 9 PM (Fri-Sat until 10 PM)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-car-line text-blue-600"></i>
                      <span>Free Parking Available</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">More from {categoryData.name}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {items.filter(i => i.slug !== itemSlug).slice(0, 3).map((relatedItem, index) => (
                  <Link
                    key={index}
                    to={`/menu/${category}/${relatedItem.slug}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 group"
                  >
                    <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                      <img
                        src={relatedItem.image}
                        alt={relatedItem.name}
                        className="w-full h-32 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {relatedItem.name}
                        </h3>
                        {relatedItem.price > 0 && (
                          <span className="text-blue-600 font-bold">
                            ${relatedItem.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{relatedItem.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MenuItemPage;
